import { useState } from 'react'

import wordmarkUrl from '@/assets/wordmark.svg'
import { pickTip } from '@/lib/tips'

/** The mark's three bars (brand/assets/mark.svg), each with when it starts speaking, in ms. */
const BARS = [
  { x: 3.5, y: 16, height: 16, delay: 0 },
  { x: 10.5, y: 10, height: 28, delay: 90 },
  { x: 17.5, y: 18, height: 12, delay: 180 },
]
const ROW_AT = 750
const NAME_AT = 1000
const TIP_AT = 300

/** One tip for this page load: the splash can mount several times while the app starts, and the tip stays the same. */
const TIP = pickTip(Math.random())

/**
 * When the splash first showed. While the app starts it is shown by the router, then the session check, then the org's
 * layout, each mounting it afresh; each mount carries the opening on from here instead of starting it again.
 */
let firstShownAt: number | null = null

/**
 * The dashboard's loading screen while it starts: the logo plays its official opening once (the bars speak and settle,
 * the row is drawn, the name rises; BRAND.md → Logo) and holds, with one tip (8 to 10 words) of something to do in
 * RowCall, a different one each load. Still, and complete, for people who ask for less motion.
 */
export const Splash = () => {
  const [elapsed] = useState(() => {
    const now = performance.now()
    firstShownAt ??= now
    return now - firstShownAt
  })
  // A negative delay starts an animation part-way through, so a remount carries on where the last one was.
  const at = (ms: number) => ({ animationDelay: `${ms - elapsed}ms` })
  return (
    <div
      className="flex min-h-svh flex-col items-center justify-center gap-6 bg-background px-4"
      role="status"
      aria-label="Loading RowCall"
    >
      <svg viewBox="0 0 202 48" aria-hidden="true" className="h-12 w-auto text-primary">
        {BARS.map((bar) => (
          <rect
            key={bar.x}
            x={bar.x}
            y={bar.y}
            width="5"
            height={bar.height}
            rx="2.5"
            fill="currentColor"
            className="origin-center transform-fill motion-safe:animate-speak"
            style={at(bar.delay)}
          />
        ))}
        <line
          x1="28"
          y1="24"
          x2="44"
          y2="24"
          stroke="currentColor"
          strokeWidth="5"
          strokeLinecap="round"
          strokeDasharray="16"
          className="motion-safe:animate-draw-row"
          style={at(ROW_AT)}
        />
        <image
          href={wordmarkUrl}
          x="59"
          y="10"
          width="142"
          height="28"
          className="motion-safe:animate-rise-in"
          style={at(NAME_AT)}
        />
      </svg>
      <p className="max-w-sm text-center text-sm text-muted-foreground motion-safe:animate-rise-in" style={at(TIP_AT)}>
        {TIP}
      </p>
    </div>
  )
}
